import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Modal,
  Input,
  Select,
  Card,
  Avatar,
  Spin,
  Empty,
  Button,
  Pagination,
  Badge,
  Tooltip,
  message,
  Tag,
} from "antd";
import {
  SearchOutlined,
  UserOutlined,
  HospitalOutlined,
  MailOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { FaUser, FaEnvelope, FaHospital, FaSearch } from "react-icons/fa";
import drEasyApi from "../../Api/Customapi";
import { getImg } from "../../Api/getImg";
import CreatePatientModal from "../Screens/PatientsList/CreatePatientModal";

const { Search } = Input;
const { Option } = Select;

// Custom debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

// Patient Card Component for Selection
const PatientSelectionCard = React.memo(
  ({ patient, onSelect, selectedPatientId, isMobile }) => {
    const isSelected = selectedPatientId === patient.id;

    const handleClick = useCallback(() => {
      onSelect(patient.id, patient);
    }, [patient, onSelect]);

    return (
      <Card
        hoverable
        onClick={handleClick}
        className={`
        relative border rounded-xl transition-all duration-300 cursor-pointer
        ${
          isSelected
            ? "border-blue-500 bg-blue-50 shadow-lg ring-2 ring-blue-200"
            : "border-gray-200 hover:border-blue-300 hover:shadow-md"
        }
        ${isMobile ? "mb-3" : ""}
      `}
      >
        {/* Selection Badge */}
        {isSelected && (
          <div className="absolute top-2 right-2 z-10">
            <Badge
              status="success"
              text={
                <span className="text-xs font-bold text-green-600">
                  Selected
                </span>
              }
              className="bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full shadow-sm"
            />
          </div>
        )}

        <div className={`${isMobile ? "p-3" : "p-4"}`}>
          {/* Patient Header */}
          <div
            className={`flex items-center mb-3 ${isMobile ? "flex-wrap" : ""}`}
          >
            <div className="relative flex-shrink-0">
              <Avatar
                src={getImg(patient.imageId)}
                size={isMobile ? 40 : 48}
                style={{ backgroundColor: "#298E9F" }}
                className="shadow-lg ring-2 ring-white"
              >
                {!getImg(patient.imageId) &&
                  patient.fullName?.charAt(0)?.toUpperCase()}
              </Avatar>
              {/* Active indicator */}
              {!patient.isDeactivated && (
                <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
              )}
            </div>

            <div className="ml-3 min-w-0 flex-1">
              <h4
                className={`font-bold text-gray-800 ${
                  isMobile ? "text-sm" : "text-base"
                } truncate`}
              >
                {patient.fullName}
              </h4>
              <div
                className={`flex items-center text-gray-500 mt-1 ${isMobile ? "text-xs" : "text-sm"}`}
              >
                <FaUser className="mr-1 flex-shrink-0" />
                <span className="truncate">ID: #{patient.id}</span>
              </div>
            </div>
          </div>

          {/* Patient Details */}
          <div className="space-y-2">
            {/* Email */}
            <div
              className={`flex items-center ${isMobile ? "text-xs" : "text-sm"} text-gray-600`}
            >
              <FaEnvelope className="mr-2 flex-shrink-0 text-blue-500" />
              <Tooltip title={patient.email}>
                <span className="truncate">{patient.email || "No email"}</span>
              </Tooltip>
            </div>

            {/* Clinic */}
            <div
              className={`flex items-center ${isMobile ? "text-xs" : "text-sm"} text-gray-600`}
            >
              <FaHospital className="mr-2 flex-shrink-0 text-green-500" />
              <span className="truncate">
                {patient.clinics?.[0]?.name || "No clinic assigned"}
              </span>
            </div>

            {/* Status */}
            <div className="flex items-center justify-between">
              <Tag
                color={!patient.isDeactivated ? "green" : "red"}
                className="text-xs"
              >
                {!patient.isDeactivated ? "Active" : "Inactive"}
              </Tag>
              {patient.skipedCount > 0 && (
                <Tag color="orange" className="text-xs">
                  Skip Count: {patient.skipedCount}
                </Tag>
              )}
            </div>
          </div>
        </div>
      </Card>
    );
  },
);

const PatientAssignmentModal = ({
  visible,
  onCancel,
  onAssign,
  loading,
  booking,
  isMobile = false,
}) => {
  const [patients, setPatients] = useState([]);
  const [clinicOptions, setClinicOptions] = useState([]);
  const [selectedClinicId, setSelectedClinicId] = useState(null);
  const [searchName, setSearchName] = useState("");
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [createPatientModalVisible, setCreatePatientModalVisible] = useState(false);

  const PAGE_SIZE = isMobile ? 6 : 8;

  // Debounce search input
  const debouncedSearchName = useDebounce(searchName, 300);

  // Fetch clinics on mount
  useEffect(() => {
    const fetchClinics = async () => {
      try {
        const res = await drEasyApi.get("/api/Clinic/my-clinics", {
          params: { PageNumber: 1, PageSize: 100 },
        });
        const clinics = res.data.data.clinics || [];
        setClinicOptions([{ id: null, name: "All Clinics" }, ...clinics]);
      } catch (error) {
        console.error("Failed to fetch clinics:", error);
      }
    };

    if (visible) {
      fetchClinics();
    }
  }, [visible]);

  // Fetch patients
  const fetchPatients = useCallback(async () => {
    setPatientsLoading(true);

    const params = {
      NameFilter: debouncedSearchName || undefined,
      ClinicId: selectedClinicId || undefined,
      deactivated: false, // Only show active patients
      Page: page,
      PageSize: PAGE_SIZE,
    };

    try {
      const res = await drEasyApi.get("/api/Centers/getPatients", {
        params,
        timeout: 10000,
      });

      const normalizedPatients = (res.data.patients || []).map((patient) => ({
        ...patient,
        isDeactivated: patient.isDeactivated ?? patient.isDeactiveted ?? false,
      }));

      setPatients(normalizedPatients);
      setTotalCount(res.data.totalCount || 0);
    } catch (error) {
      console.error("Fetch patients error:", error);
      // message.error('Failed to fetch patients');
    } finally {
      setPatientsLoading(false);
    }
  }, [debouncedSearchName, selectedClinicId, page, PAGE_SIZE]);

  useEffect(() => {
    if (visible) {
      fetchPatients();
    }
  }, [visible, fetchPatients]);

  useEffect(() => {
    if (visible) {
      setSearchName("");
      setSelectedClinicId(null);
      setSelectedPatientId(null);
      setSelectedPatient(null);
      setPage(1);
    }
  }, [visible]);

  const handleSearchChange = (value) => {
    setSearchName(value);
    setPage(1);
  };

  const handleClinicChange = (value) => {
    setSelectedClinicId(value);
    setPage(1);
  };

  const handlePatientSelect = useCallback((patientId, patient) => {
    console.log(patient, "Sa");

    setSelectedPatientId(patient.accountId);
    setSelectedPatient(patient);
  }, []);

  // Updated handleAssign function with real API call
  const handleAssign = async () => {
    if (!selectedPatientId || !selectedPatient) {
      message.warning("Please select a patient first");
      return;
    }

    if (!booking?.id) {
      message.error("Booking information is missing");
      return;
    }

    setAssignLoading(true);

    try {
      // Call the real API endpoint
      const response = await drEasyApi.put("/api/Book/AssignBooking", {
        id: booking.id, // Guest booking ID
        userId: selectedPatientId, // Selected patient/user ID to assign to
      });

      // Log successful response for debugging
      console.log("Assignment successful:", response.data);

      // Call the parent callback with the selected patient
      onAssign(selectedPatient);
    } catch (error) {
      console.error("Failed to assign guest to patient:", error);

      // Handle different error scenarios
      if (error.response) {
        const { status, data } = error.response;

        switch (status) {
          case 400:
            message.error(
              data?.message ||
                "Invalid request. Please check the booking and patient selection.",
            );
            break;
          case 404:
            message.error(
              "Booking or patient not found. Please refresh and try again.",
            );
            break;
          case 409:
            message.error(
              "This booking has already been assigned or is no longer available.",
            );
            break;
          case 403:
            message.error("You do not have permission to assign this booking.");
            break;
          default:
            message.error(
              `Assignment failed: ${data?.message || "Unknown error occurred"}`,
            );
        }
      } else if (error.request) {
        message.error(
          "Network error. Please check your connection and try again.",
        );
      } else {
        message.error("An unexpected error occurred. Please try again.");
      }
    } finally {
      setAssignLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchName("");
    setSelectedClinicId(null);
    setPage(1);
  };

  return (
    <Modal
      title={
        <div className="flex items-center justify-between pt-2">
          <div className="flex items-center ">
            <div
              className={`${isMobile ? "w-6 h-6" : "w-8 h-8"} bg-primary rounded-full flex items-center justify-center mr-3`}
            >
              <UserOutlined
                className={`text-white ${isMobile ? "text-xs" : "text-sm"}`}
              />
            </div>
            <div>
              <span className={isMobile ? "text-sm" : "text-base"}>
                Assign Guest to Patient
              </span>
              {booking && (
                <div
                  className={`text-gray-500 ${isMobile ? "text-xs" : "text-sm"} font-normal mt-1`}
                >
                  Guest: {booking.patientName}
                </div>
              )}
            </div>
          </div>
          <Button
            type=""
            icon={<PlusOutlined />}
            className="bg-primary text-white flex items-center gap-2 justify-center mt-1.5 me-2"
            onClick={setCreatePatientModalVisible}
            size={isMobile ? "middle" : "middle"}
          >
            <span className="hidden sm:inline">Add New Patient</span>
          </Button>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={assignLoading}>
          Cancel
        </Button>,
        <Button
          key="assign"
          type=""
          onClick={handleAssign}
          loading={assignLoading}
          disabled={!selectedPatientId || assignLoading}
          className="bg-primary text-white"
        >
          {assignLoading ? "Assigning..." : "Assign to Patient"}
        </Button>,
      ]}
      width={isMobile ? "95vw" : 800}
      className="patient-assignment-modal"
      centered={isMobile}
      destroyOnClose
      maskClosable={!assignLoading}
      closable={!assignLoading}
    >
      <div className="space-y-4">
        {/* Search and Filter Controls */}
        <div
          className={`${isMobile ? "space-y-3" : "grid grid-cols-1 md:grid-cols-2 gap-4"}`}
        >
          {/* Search */}
          <div>
            <label
              className={`block text-gray-700 font-medium mb-2 ${isMobile ? "text-sm" : ""}`}
            >
              Search Patients:
            </label>
            <Search
              placeholder="Search by patient name..."
              allowClear
              value={searchName}
              onChange={(e) => handleSearchChange(e.target.value)}
              prefix={<FaSearch className="text-gray-400" />}
              size={isMobile ? "middle" : "large"}
              className="rounded-lg"
              disabled={assignLoading}
            />
          </div>

          {/* Clinic Filter */}
          <div>
            <label
              className={`block text-gray-700 font-medium mb-2 ${isMobile ? "text-sm" : ""}`}
            >
              Filter by Clinic:
            </label>
            <Select
              placeholder="All Clinics"
              allowClear
              value={selectedClinicId}
              onChange={handleClinicChange}
              size={isMobile ? "middle" : "large"}
              className="w-full rounded-lg"
              disabled={assignLoading}
            >
              {clinicOptions.map((clinic) => (
                <Option key={clinic.clinicId ?? "all"} value={clinic.clinicId}>
                  {clinic.clinicName}
                </Option>
              ))}
            </Select>
          </div>
        </div>

        {/* Clear Filters */}
        {(searchName || selectedClinicId) && (
          <div className="flex justify-end">
            <Button
              size="small"
              onClick={clearFilters}
              disabled={assignLoading}
            >
              Clear Filters
            </Button>
          </div>
        )}

        {/* Patients List */}
        <div
          className={`border rounded-lg ${isMobile ? "max-h-80" : "max-h-96"} overflow-y-auto`}
        >
          <Spin spinning={patientsLoading}>
            {patients.length > 0 ? (
              <div
                className={`${isMobile ? "p-3 space-y-3" : "p-4 grid grid-cols-1 md:grid-cols-2 gap-4"}`}
              >
                {patients.map((patient) => (
                  <PatientSelectionCard
                    key={patient.id}
                    patient={patient}
                    onSelect={handlePatientSelect}
                    selectedPatientId={selectedPatientId}
                    isMobile={isMobile}
                  />
                ))}
              </div>
            ) : (
              !patientsLoading && (
                <div className="p-8">
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No patients found"
                    className="my-4"
                  >
                    {(searchName || selectedClinicId) && (
                      <Button
                        type="primary"
                        onClick={clearFilters}
                        disabled={assignLoading}
                      >
                        Clear Filters
                      </Button>
                    )}
                  </Empty>
                </div>
              )
            )}
          </Spin>
        </div>

        {/* Pagination */}
        {totalCount > PAGE_SIZE && (
          <div className="flex justify-center pt-4 border-t">
            <Pagination
              current={page}
              pageSize={PAGE_SIZE}
              total={totalCount}
              onChange={setPage}
              showTotal={(total) => (
                <span
                  className={`text-gray-600 ${isMobile ? "text-xs" : "text-sm"}`}
                >
                  {isMobile ? `${total} total` : `Total ${total} patients`}
                </span>
              )}
              size={isMobile ? "small" : "default"}
              showSizeChanger={false}
              showQuickJumper={!isMobile}
              disabled={assignLoading}
            />
          </div>
        )}

        {/* Selected Patient Info */}
        {selectedPatient && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
            <h4
              className={`font-medium text-blue-800 mb-2 ${isMobile ? "text-sm" : ""}`}
            >
              Selected Patient:
            </h4>
            <div className="flex items-center">
              <Avatar
                src={getImg(selectedPatient.imageId)}
                size={isMobile ? 32 : 40}
                style={{ backgroundColor: "#298E9F" }}
                className="mr-3"
              >
                {!getImg(selectedPatient.imageId) &&
                  selectedPatient.fullName?.charAt(0)?.toUpperCase()}
              </Avatar>
              <div>
                <div
                  className={`font-medium text-gray-800 ${isMobile ? "text-sm" : ""}`}
                >
                  {selectedPatient.fullName}
                </div>
                <div
                  className={`text-gray-600 ${isMobile ? "text-xs" : "text-sm"}`}
                >
                  {selectedPatient.email}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Assignment Progress Info */}
        {assignLoading && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-4">
            <div className="flex items-center">
              <Spin size="small" className="mr-2" />
              <span className={`text-yellow-800 ${isMobile ? "text-sm" : ""}`}>
                Assigning guest booking to {selectedPatient?.fullName}...
              </span>
            </div>
          </div>
        )}
      </div>
        <CreatePatientModal
        visible={createPatientModalVisible}
        onClose={() => {
          setCreatePatientModalVisible(false);
          fetchPatients(); // ← يعمل refresh للـ list بعد الإضافة
        }}
        isMobile={isMobile}
      />
    </Modal>
  );
};

PatientSelectionCard.displayName = "PatientSelectionCard";

export default PatientAssignmentModal;
